import { Reveal, SectionHeading } from "@/components/Reveal";
import { EditorialMarquee } from "@/components/EditorialMarquee";
import { waLink } from "@/lib/site";

const STATS = [
  { value: "2011", label: "Founded in Rajkot" },
  { value: "12,000+", label: "Happy travellers" },
  { value: "125+", label: "Destinations served" },
  { value: "4.8★", label: "Average rating" },
];

export default function About() {
  return (
    <div className="pt-32 pb-0" data-testid="about-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <SectionHeading overline="Our Story" title={<>Travel people, <span className="font-light italic">not a travel portal.</span></>} className="mb-16" />
        <div className="grid lg:grid-cols-2 gap-14 items-start mb-24">
          <Reveal>
            <div className="space-y-5 text-ink/70 leading-relaxed">
              <p className="text-xl text-ink font-light">Click2trip began in 2011 as a two-desk agency in Rajkot with one stubborn belief: <span className="font-semibold">planning a holiday should feel as good as the holiday itself.</span></p>
              <p>Fifteen years and twelve thousand travellers later, that belief hasn't changed. We still hand-check every hotel, still ride the routes we recommend, and still answer WhatsApp messages at midnight before a 6 AM flight.</p>
              <p>Our mission is simple — make honest, human travel planning accessible to everyone. No inflated "discounted" prices, no hidden fees, no call-center runarounds. Just a team that treats your trip like their own.</p>
              <p>Today we serve 125+ destinations across India and the world, with specialist desks for honeymoons, corporate travel and group tours.</p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="overflow-hidden rounded-3xl aspect-[4/5]">
                <img src="https://images.pexels.com/photos/14568458/pexels-photo-14568458.jpeg" alt="Kerala backwaters — a click2trip destination" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-8 -left-4 sm:-left-8 bg-ink text-bone rounded-2xl p-6 grain relative overflow-hidden max-w-xs">
                <p className="font-display text-4xl font-black text-sun">15+</p>
                <p className="text-sm text-bone/70">years of crafting journeys, one traveller at a time</p>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-24" data-testid="stats-row">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <div className="border-t-2 border-sun pt-5" data-testid={`stat-${i}`}>
                <p className="font-display text-4xl sm:text-5xl font-black">{s.value}</p>
                <p className="text-ink/60 text-sm mt-1">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <SectionHeading overline="The Founder" title={<>The face behind <span className="font-light italic">your trips.</span></>} className="mb-12" />
        <Reveal className="mb-24">
          <div className="grid md:grid-cols-2 bg-white border border-border rounded-3xl overflow-hidden" data-testid="founder-card">
            <div className="aspect-square md:aspect-auto overflow-hidden">
              <img src="/founder.jpg" alt="Piyush Jivrajani, Founder of click2trip" loading="lazy"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="p-8 md:p-12 flex flex-col justify-center">
              <p className="text-xs uppercase tracking-[0.25em] text-sun mb-3">Founder</p>
              <h3 className="font-display text-3xl sm:text-4xl font-black mb-1" data-testid="founder-name">Piyush Jivrajani</h3>
              <p className="text-ink/50 text-sm mb-6">Founder, click2trip</p>
              <p className="text-ink/70 leading-relaxed mb-8">
                "Travel has been my life's work since 2011. Every itinerary we craft carries a personal promise — honest pricing, verified stays, and a human on the other end of WhatsApp whenever you need one. Your happiness is, quite literally, our business."
              </p>
              <a href={waLink("Hi Piyush! I'd like to plan a trip with click2trip.")} target="_blank" rel="noreferrer" data-testid="founder-cta-btn"
                className="inline-flex w-fit bg-sun text-bone font-semibold px-7 py-3 rounded-full hover:bg-[#e85a28] hover:scale-[1.02] transition-all text-sm">
                Plan a Trip With Us
              </a>
            </div>
          </div>
        </Reveal>
      </div>
      <EditorialMarquee dark={false} />
      <div className="bg-ink py-20 text-center">
        <Reveal>
          <h2 className="font-display text-4xl sm:text-5xl font-black text-bone mb-6">Ready when you are.</h2>
          <a href={waLink("Hi click2trip! I'd like to plan a trip.")} target="_blank" rel="noreferrer" data-testid="about-cta-btn"
            className="inline-flex bg-sun text-bone font-semibold px-9 py-4 rounded-full hover:scale-105 active:scale-95 transition-transform">
            Start Planning
          </a>
        </Reveal>
      </div>
    </div>
  );
}
